import React from 'react';
import DatePicker from './DatePicker';
import './DateTimePicker.css';

export interface DateTimePickerProps {
  value: string; // ISO string
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
}

function isoToLocal(iso: string): { date: string; hour: number; minute: number } {
  const trimmed = (iso || '').trim();
  if (!trimmed) {
    return { date: '', hour: 0, minute: 0 };
  }
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) {
    return { date: '', hour: 0, minute: 0 };
  }
  const date =
    `${d.getFullYear()}-` +
    `${String(d.getMonth() + 1).padStart(2, '0')}-` +
    `${String(d.getDate()).padStart(2, '0')}`;
  return { date, hour: d.getHours(), minute: d.getMinutes() };
}

function toISO(date: string, hour: number, minute: number): string {
  const trimmed = (date || '').trim();
  if (!trimmed) return '';

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return '';

  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '';

  const h = Number.isFinite(hour) ? Math.min(23, Math.max(0, Math.trunc(hour))) : 0;
  const min = Number.isFinite(minute) ? Math.min(59, Math.max(0, Math.trunc(minute))) : 0;

  const dt = new Date(y, m - 1, d, h, min, 0, 0);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toISOString();
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

const DateTimePicker: React.FC<DateTimePickerProps> = ({ value, onChange, label, disabled, min, max }) => {
  const { date, hour, minute } = isoToLocal(value);

  const setDate = (v: string) => onChange(toISO(v, hour, minute));
  const setHour = (h: number) => onChange(toISO(date, h, minute));
  const setMinute = (m: number) => onChange(toISO(date, hour, m));

  return (
    <div className="datetime-picker">
      {label && <label className="datetime-picker-label">{label}</label>}
      <div className="datetime-picker-row">
        <DatePicker
          value={date}
          onChange={setDate}
          placeholder="Date"
          min={min ? isoToLocal(min).date : undefined}
          max={max ? isoToLocal(max).date : undefined}
          disabled={disabled}
          className="datetime-picker-date"
        />
        <div className="datetime-picker-time">
          <select
            className="datetime-picker-select"
            value={hour}
            onChange={(e) => setHour(Number(e.target.value))}
            disabled={disabled}
            aria-label="Hour"
          >
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, '0')}
              </option>
            ))}
          </select>
          <span className="datetime-picker-sep">:</span>
          <select
            className="datetime-picker-select"
            value={minute}
            onChange={(e) => setMinute(Number(e.target.value))}
            disabled={disabled}
            aria-label="Minute"
          >
            {MINUTES.map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, '0')}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default DateTimePicker;
